/**
 * useSessionTracking — registers a tracked session for the current visitor.
 *
 * Behavior:
 *   - On mount, generates (or reuses) a UUID stored in `sessionStorage` under
 *     `app_session_id` plus an `app_anonymous_id` in `localStorage` that
 *     persists across sessions for return-visit tracking.
 *   - Sends an initial /api/sessions/track call (creates the session row).
 *   - Sends a heartbeat every HEARTBEAT_MS while the tab is visible. Pauses
 *     while the tab is hidden so we don't inflate session duration.
 *   - On `pagehide` (tab close, navigation), uses navigator.sendBeacon to
 *     finalize the session with the measured visible duration.
 *
 * The hook is fire-and-forget; failures are silent — analytics must never
 * break the user experience.
 */
import { useEffect, useRef } from 'react';

const HEARTBEAT_MS = 30 * 1000;
const SESSION_KEY = 'app_session_id';
const ANON_KEY = 'app_anonymous_id';

function uuid() {
  // sessionStorage-friendly UUID; uses crypto when available.
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return (
    Math.random().toString(36).slice(2, 14) +
    Math.random().toString(36).slice(2, 14)
  );
}

function getOrCreateSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = uuid();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return uuid();
  }
}

function getOrCreateAnonymousId() {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return uuid();
  }
}

export default function useSessionTracking({ apiBase, getMunicipioId, getAuthHeader }) {
  // Refs avoid re-creating timers on each render.
  const sessionIdRef = useRef(null);
  const anonymousIdRef = useRef(null);
  const visibleStartRef = useRef(null);
  const visibleAccumRef = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!apiBase) return undefined;
    sessionIdRef.current = getOrCreateSessionId();
    anonymousIdRef.current = getOrCreateAnonymousId();
    visibleStartRef.current = document.visibilityState === 'visible' ? Date.now() : null;

    const trackUrl = `${apiBase}/api/sessions/track`;
    const endUrl = `${apiBase}/api/sessions/end`;

    const buildHeaders = () => {
      const h = { 'Content-Type': 'application/json' };
      try {
        const auth = getAuthHeader && getAuthHeader();
        if (auth) h.Authorization = auth;
      } catch { /* ignore */ }
      return h;
    };

    const buildPayload = () => ({
      session_id: sessionIdRef.current,
      anonymous_id: anonymousIdRef.current,
      municipio_id: (getMunicipioId && getMunicipioId()) || null,
    });

    const send = (url, payload) => {
      try {
        fetch(url, {
          method: 'POST',
          headers: buildHeaders(),
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => { /* silent */ });
      } catch { /* silent */ }
    };

    // Initial track + heartbeat loop (only while visible).
    send(trackUrl, buildPayload());
    intervalRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        send(trackUrl, buildPayload());
      }
    }, HEARTBEAT_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        visibleStartRef.current = Date.now();
        send(trackUrl, buildPayload());
      } else if (visibleStartRef.current) {
        visibleAccumRef.current += Date.now() - visibleStartRef.current;
        visibleStartRef.current = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const computeDuration = () => {
      let total = visibleAccumRef.current;
      if (visibleStartRef.current) {
        total += Date.now() - visibleStartRef.current;
      }
      return Math.max(0, Math.floor(total / 1000));
    };

    const onPageHide = () => {
      const payload = JSON.stringify({
        session_id: sessionIdRef.current,
        duracao_segundos: computeDuration(),
      });
      // sendBeacon is more reliable than fetch on tab close.
      try {
        if (navigator.sendBeacon) {
          const blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon(endUrl, blob);
          return;
        }
      } catch { /* ignore */ }
      fetch(endUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => { /* silent */ });
    };
    window.addEventListener('pagehide', onPageHide);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [apiBase, getMunicipioId, getAuthHeader]);
}
